import { ActionIcon, Badge, Box, Group, Loader, Modal, Text, Tooltip } from "@mantine/core";
import { formatIssueFormulaGeneratedString } from "@mcapps/shared/issueFormulaUtils";
import { IIssueApi } from "@schemas/issueSchema";
import { ICapitalLetter } from "@schemas/otherAttributesSchema";
import { IconListDetails } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import {
  GetProjectByProjectIdIssueFormulaGroupIssuesApiResponse,
  useGetProjectByProjectIdIssueFormulaGroupIssuesQuery,
} from "../../../../../../../../openapi";
import { Avatar } from "../../../../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../../../../hooks";
import { IssueIdWithAksjonCompletedCount } from "../../../../IssueIdWithAksjonCompletedCount";
import { formatShortDate } from "../../../../../admin/selectors";
import { selectCurrentOpenIssueBoardInModal, selectCurrentOpenIssueInModal } from "../../../../issueSelectors";
import { setFormulaCounterGroupIssuesModalInRedux, thunkOpenIssueModalById } from "../../../../issueSlice";
import { getIssueRegisterNameOption } from "../../../../issueUtils";
import { selectFormulaCounterGroupIssuesBaseArgsInModal } from "./issueFormulaCounterSuggestion.selectors";

type FormulaGroupIssue = GetProjectByProjectIdIssueFormulaGroupIssuesApiResponse["issues"][number];

const IssueFormulaCounterGroupIssuesModal = ({ formulaAttributeKey }: { formulaAttributeKey: ICapitalLetter }) => {
  const dispatch = useAppDispatch();
  const issue = useAppSelector(selectCurrentOpenIssueInModal);
  const issueBoard = useAppSelector(selectCurrentOpenIssueBoardInModal);
  const baseArgs = useAppSelector(selectFormulaCounterGroupIssuesBaseArgsInModal);
  const formula = issueBoard?.otherAttributesConfig?.[formulaAttributeKey]?.formula;
  const issuePluralName = getIssueRegisterNameOption(issueBoard?.issueNameSetting, "plural") ?? "Saker";
  const issuePluralNameLower = issuePluralName.toLowerCase();
  const openedModal = useAppSelector((state) => state.issueReducerNew.formulaCounterGroupIssuesModal);
  const opened = openedModal?.issueId === issue?._id && openedModal.formulaAttributeKey === formulaAttributeKey;

  const { data, isFetching } = useGetProjectByProjectIdIssueFormulaGroupIssuesQuery(
    {
      projectId: baseArgs?.projectId ?? "",
      issueId: issue?._id ?? "",
      formulaAttributeKey,
    },
    {
      skip: !opened || issue == null || baseArgs == null || formula == null,
    },
  );

  const columnDefs = useMemo<ColDef<FormulaGroupIssue>[]>(() => {
    const formulaAttributeColumns =
      formula?.confArr
        .filter((conf) => conf.type === "ATTRIBUTE" && conf.value)
        .map<ColDef<FormulaGroupIssue>>((conf) => ({
          headerName: issueBoard?.otherAttributesConfig?.[conf.value]?.label ?? conf.value,
          flex: 1,
          minWidth: 140,
          valueGetter: (params) => {
            const value =
              conf.value === "title" || conf.value === "bucket" || conf.value === "status"
                ? params.data?.[conf.value]
                : params.data?.otherAttributesData?.[conf.value];

            return Array.isArray(value) ? value.join(", ") : value ?? "";
          },
        })) ?? [];

    return [
      {
        field: "_id",
        hide: true,
      },
      {
        field: "id",
        headerName: "Id",
        width: 110,
        pinned: "left",
        cellClass: "grid-display-block px-1",
        cellRenderer: (params) => {
          if (params.data == null) return null;

          return (
            <IssueIdWithAksjonCompletedCount
              id={params.value}
              issue={{ ...params.data, taskOrIssue: "ISSUE" } as IIssueApi}
              setSelectedIssue={(selectedIssue) => dispatch(thunkOpenIssueModalById(selectedIssue._id))}
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Opprettet",
        width: 140,
        valueFormatter: (params) => (params.value ? formatShortDate(params.value, true) : ""),
      },
      {
        headerName: "Opprettet av",
        width: 180,
        cellRenderer: (params) => {
          const user = params.data?.createdBy;
          if (user == null) return null;

          return (
            <div className="d-flex align-items-center">
              <Avatar id={user._id} name={user.name} />
              <div className="ml-2">{user.name}</div>
            </div>
          );
        },
      },
      ...formulaAttributeColumns,
      {
        headerName: "Löpnummer",
        width: 140,
        valueGetter: (params) => params.data?.formulaCounters?.[formulaAttributeKey]?.value ?? "",
      },
      {
        headerName: "Beregnet verdi",
        flex: 2,
        minWidth: 260,
        valueGetter: (params) => {
          if (formula == null || issueBoard == null || params.data == null) return "";

          return formatIssueFormulaGeneratedString({
            formula,
            countForIssue: params.data.formulaCounters?.[formulaAttributeKey]?.value ?? null,
            deletePreviousDelimiterIfAttributeIsEmpty: formula.deletePreviousDelimiterIfAttributeIsEmpty ?? false,
            issue: params.data as Partial<IIssueApi>,
            otherAttributesConfig: issueBoard.otherAttributesConfig,
          });
        },
      },
    ];
  }, [dispatch, formula, formulaAttributeKey, issueBoard]);

  return (
    <>
      <Tooltip label={`Vis ${issuePluralNameLower} i samme formelgruppe`} withArrow zIndex={2042}>
        <ActionIcon
          color="orange"
          variant="light"
          size="lg"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (issue?._id == null) return;

            dispatch(
              setFormulaCounterGroupIssuesModalInRedux({
                issueId: issue._id,
                formulaAttributeKey,
              }),
            );
          }}
        >
          <IconListDetails size={18} />
        </ActionIcon>
      </Tooltip>

      <Modal
        opened={opened}
        onClose={() => dispatch(setFormulaCounterGroupIssuesModalInRedux(null))}
        title={`${issuePluralName} i samme formelgruppe`}
        size="95vw"
      >
        <Group justify="space-between" mb="sm">
          <Text size="sm" c="dimmed">
            Viser {issuePluralNameLower} som matcher formelgruppen for dette løpnummeret.
          </Text>
          {isFetching ? (
            <Loader size="xs" color="orange" />
          ) : (
            <Badge color="orange" variant="light">
              {data?.issues.length ?? 0} {issuePluralNameLower}
            </Badge>
          )}
        </Group>
        <Box className="ag-theme-quartz" h={500} w="100%">
          <AgGridReact<FormulaGroupIssue>
            rowData={data?.issues ?? []}
            columnDefs={columnDefs}
            getRowStyle={(params) =>
              params.data?._id === issue?._id
                ? {
                    backgroundColor: "var(--mantine-color-orange-0)",
                    fontWeight: 700,
                  }
                : undefined
            }
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default IssueFormulaCounterGroupIssuesModal;
